import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDate, formatTime } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { intelApi } from "@/lib/intel";

const TYPE_COLORS = {
  Hearing: "bg-copper text-white", Deadline: "bg-danger text-white", Limitation: "bg-danger text-white",
  Task: "bg-info text-white", Compliance: "bg-warning text-white", Request: "bg-success text-white", "Client Meeting": "bg-success text-white",
  "ODR Session": "bg-navy text-white",
};
const iso = (d) => d.toISOString().slice(0, 10);

export default function Calendar() {
  const { db, persona } = useApp();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("Month");
  const [detail, setDetail] = useState(null);
  const [typeFilter, setTypeFilter] = useState([]);
  const [feed, setFeed] = useState({ events: [], loading: true });

  // Matter → Calendar sync: one server-side scoped feed (hearings, limitation, tasks, compliance, requests, ODR) for the visible window.
  const range = useMemo(() => { const y = cursor.getFullYear(), m = cursor.getMonth(); return [iso(new Date(y, m - 1, 20)), iso(new Date(y, m + 3, 10))]; }, [cursor]);
  const refresh = React.useCallback(() => { setFeed((f) => ({ ...f, loading: true })); intelApi.calendar(range[0], range[1]).then((d) => setFeed({ events: d.events, loading: false, scope: d.scope })).catch(() => setFeed({ events: [], loading: false, error: true })); }, [range]);
  useEffect(() => { refresh(); }, [refresh, db.hearings.length, db.tasks.length, db.orders.length, persona?.id]);
  const events = feed.events;

  const shown = typeFilter.length ? events.filter((e) => typeFilter.includes(e.type)) : events;
  const types = ["Hearing", "Task", "Limitation", "Compliance", "Request", "ODR Session"];

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const eventsOn = (date) => shown.filter((e) => new Date(e.date).toDateString() === date.toDateString());
  const upcoming = [...shown].filter((e) => new Date(e.date) >= new Date()).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 6);
  // MOD-09: real Week / Day grids
  const weekStart = new Date(cursor); weekStart.setHours(0, 0, 0, 0); weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d; });
  const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 08:00 – 20:00
  const hourOf = (e) => { const d = new Date(e.date); return d.getHours() + d.getMinutes() / 60; };
  const hasTime = (e) => { const d = new Date(e.date); return !(d.getHours() === 0 && d.getMinutes() === 0) || e.type === "Hearing"; };
  const step = (dir) => {
    if (view === "Week") setCursor(new Date(year, month, cursor.getDate() + 7 * dir));
    else if (view === "Day") setCursor(new Date(year, month, cursor.getDate() + dir));
    else setCursor(new Date(year, month + dir, 1));
  };
  const heading = view === "Week" ? `${formatDate(weekDays[0], "dd MMM")} – ${formatDate(weekDays[6], "dd MMM yyyy")}` : view === "Day" ? formatDate(cursor, "EEEE, dd MMMM yyyy") : cursor.toLocaleString("en-IN", { month: "long", year: "numeric" });
  const EventChip = ({ e }) => <button onClick={() => setDetail(e)} className={cn("block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px]", TYPE_COLORS[e.type])} data-testid={`cal-event-${e.id}`}>{hasTime(e) && <span className="mr-1 opacity-80">{formatTime(e.date)}</span>}{e.title}</button>;
  const TimeGrid = ({ days }) => (
    <div className="overflow-x-auto p-3" data-testid={`calendar-${view.toLowerCase()}-grid`}>
      <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` }}>
        <div />
        {days.map((d) => <div key={d} className={cn("border-b border-line px-1 pb-1 text-center text-xs font-medium", d.toDateString() === new Date().toDateString() ? "text-copper" : "text-ink-2")}>{formatDate(d, days.length > 1 ? "EEE dd" : "EEEE dd MMM")}</div>)}
        <div className="border-b border-line px-1 pb-1 text-[10px] text-ink-muted">All day</div>
        {days.map((d) => <div key={`ad-${d}`} className="min-h-[28px] border-b border-l border-line p-0.5">{eventsOn(d).filter((e) => !hasTime(e)).map((e) => <EventChip key={e.id} e={e} />)}</div>)}
        {HOURS.map((h) => (
          <React.Fragment key={h}>
            <div className="h-12 border-b border-line pr-1 text-right text-[10px] text-ink-muted">{String(h).padStart(2, "0")}:00</div>
            {days.map((d) => <div key={`${h}-${d}`} className="h-12 border-b border-l border-line p-0.5">{eventsOn(d).filter((e) => hasTime(e) && Math.floor(hourOf(e)) === h).map((e) => <EventChip key={e.id} e={e} />)}</div>)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Calendar" subtitle="Hearings, deadlines, limitation and compliance in one view.">
        <span className="text-xs text-ink-muted" data-testid="calendar-sync-status">{feed.loading ? "Syncing from matters…" : feed.error ? "Sync failed" : `${events.length} synced from ${feed.scope === "client" ? "your matters" : "matters"}`}</span>
        <Button variant="outline" size="icon" onClick={refresh} title="Refresh" data-testid="calendar-refresh"><RefreshCw className={cn("h-4 w-4", feed.loading && "animate-spin")} /></Button>
        <div className="flex gap-1">{["Month", "Week", "Day", "Agenda"].map((v) => <button key={v} onClick={() => setView(v)} className={cn("rounded-md px-3 py-1 text-xs font-medium", view === v ? "bg-navy text-white" : "border border-line text-ink-2")} data-testid={`calendar-view-${v.toLowerCase()}`}>{v}</button>)}</div>
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div className="space-y-4 lg:col-span-1">
          <Panel className="p-4">
            <div className="mb-2 text-xs font-semibold uppercase text-ink-2">Event Types</div>
            <div className="space-y-1.5">{types.map((t) => (
              <label key={t} className="flex cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" checked={typeFilter.includes(t)} onChange={() => setTypeFilter((f) => f.includes(t) ? f.filter((x) => x !== t) : [...f, t])} />
                <span className={cn("h-2.5 w-2.5 rounded-full", TYPE_COLORS[t]?.split(" ")[0])} />{t}
              </label>
            ))}</div>
          </Panel>
          <Panel className="p-4">
            <div className="mb-2 text-xs font-semibold uppercase text-ink-2">Upcoming Deadlines</div>
            <div className="space-y-2">{upcoming.map((e) => (
              <button key={e.id} onClick={() => setDetail(e)} className="block w-full text-left text-sm hover:text-copper">
                <div className="font-medium text-ink">{formatDate(e.date, "dd MMM")} · {e.type}</div><div className="truncate text-xs text-ink-2">{e.title}</div>
              </button>
            ))}</div>
          </Panel>
        </div>

        <Panel className="lg:col-span-3">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h3 className="font-display text-xl font-semibold text-ink" data-testid="calendar-heading">{heading}</h3>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" onClick={() => step(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
              <Button variant="outline" size="icon" onClick={() => step(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          {view === "Week" ? <TimeGrid days={weekDays} /> : view === "Day" ? <TimeGrid days={[cursor]} /> : view === "Agenda" ? (
            <div className="divide-y divide-line">{[...shown].sort((a, b) => new Date(a.date) - new Date(b.date)).map((e) => (
              <button key={e.id} onClick={() => setDetail(e)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface">
                <span className="w-24 text-xs text-ink-2">{formatDate(e.date, "dd MMM")}</span><span className={cn("rounded px-1.5 py-0.5 text-[10px]", TYPE_COLORS[e.type])}>{e.type}</span><span className="flex-1 truncate text-sm text-ink">{e.title}</span>
              </button>
            ))}</div>
          ) : (
            <div className="p-3">
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-ink-muted">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-1">{d}</div>)}</div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((date, i) => (
                  <div key={i} className={cn("min-h-[86px] rounded-lg border border-line p-1", !date && "border-transparent", date && date.toDateString() === new Date().toDateString() && "border-copper/50 bg-copper-light/30")}>
                    {date && <>
                      <div className="px-1 text-xs text-ink-2">{date.getDate()}</div>
                      <div className="space-y-0.5">{eventsOn(date).slice(0, 3).map((e) => (
                        <button key={e.id} onClick={() => setDetail(e)} className={cn("block w-full truncate rounded px-1 py-0.5 text-left text-[10px]", TYPE_COLORS[e.type])} data-testid={`cal-event-${e.id}`}>{e.title}</button>
                      ))}
                      {eventsOn(date).length > 3 && <div className="px-1 text-[10px] text-ink-muted">+{eventsOn(date).length - 3} more</div>}</div>
                    </>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent>{detail && <>
          <SheetHeader><SheetTitle className="font-display text-2xl">{detail.title}</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-2 text-sm">
            <div><span className="text-ink-2">Type:</span> <span className={cn("rounded px-1.5 py-0.5 text-[10px]", TYPE_COLORS[detail.type])}>{detail.type}</span></div>
            <div><span className="text-ink-2">When:</span> {formatDate(detail.date)} · {formatTime(detail.date)}</div>
            <div className="text-ink-2">{detail.sub}</div>
            {detail.caseNumber && <div className="text-xs text-ink-muted">{detail.caseNumber}{detail.matterTitle ? ` · ${detail.matterTitle}` : ""}</div>}
            {detail.link && <Button className="mt-3" onClick={() => { setDetail(null); navigate(detail.link); }} data-testid="calendar-open-link">{detail.matterId ? "Open Matter" : "Open"}</Button>}
          </div>
        </>}</SheetContent>
      </Sheet>
    </div>
  );
}
